import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '@cencosud-cencommerce/paris-uikit';

import setOnBehalf from '@/modules/buy-now/application/setOnBehalf';
import { createBuyNowRepository } from '@/modules/buy-now/infrastructure/BuyNowReduxRepository';
import type { AuthorizedPerson } from '@/modules/cart/domain/Cart';
import {
  TOAST_ADD_PICKUP_PERSON_SUCCESS,
  TOAST_DELETE_SUCCESS
} from '@/Pages/BuyNowPage/PaymentAndShipping/partials/PickupOptions/PickupPerson/constants';
import { useCustomerContext } from '@/sections/customer/CustomerContext';

interface Entity {
  deliveryGroupId: string;
  authorizedPerson: AuthorizedPerson;
}

interface MutatorHook<TEntity, TReturn> {
  trigger: (entity: TEntity) => Promise<TReturn>;
  isLoading: boolean;
  error: string | null;
}

export const useSetOnBehalfBuyNow = (): MutatorHook<Entity, void> => {
  const { cartId } = useParams<{ cartId: string }>();
  const buyNowRepository = createBuyNowRepository();
  const {
    customer: { data: customer }
  } = useCustomerContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isRegistered = customer !== null;

  const trigger = async ({ deliveryGroupId, authorizedPerson }: Entity) => {
    setIsLoading(true);

    if (!cartId) {
      console.warn('cartId is undefined and cannot set on behalf for buy now');
      return;
    }

    try {
      const onBehalf = await setOnBehalf(
        buyNowRepository,
        cartId,
        deliveryGroupId,
        authorizedPerson,
        isRegistered
      );
      if (onBehalf) {
        const isAdd =
          Boolean(authorizedPerson?.rut) ||
          Boolean(authorizedPerson?.documentID);
        if (isAdd) {
          toast(TOAST_ADD_PICKUP_PERSON_SUCCESS);
        } else {
          toast(TOAST_DELETE_SUCCESS);
        }
        setError(null);
      }
    } catch (error) {
      console.error('Error setting on behalf for buy now:', error);
      setError(error instanceof Error ? error.message : 'Unknown error');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { trigger, isLoading, error };
};
